import { css } from 'styled-components'

/**
 * Visible horizontal scrollbar for mouse users.
 * Overlay scrollbars (macOS/trackpad) are easy to miss; this forces a thin,
 * always-styled bar whenever the container actually overflows (overflow: auto).
 *
 * Note: use non-macro `css` so interpolations stay as real JS references
 * (styled-components/macro can inline them and trigger no-unused-vars).
 */
export const visibleHorizontalScroll = css`
  overflow-x: auto;
  overflow-y: visible;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.45) #f0f0f0;

  &::-webkit-scrollbar {
    height: 12px;
    -webkit-appearance: none;
  }
  &::-webkit-scrollbar-track {
    background: #f0f0f0;
    border-radius: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.35);
    border-radius: 6px;
    border: 2px solid #f0f0f0;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.5);
  }
`

/** Apply to antd Table scroll containers inside a wrapper. */
export const antdTableHorizontalScroll = css`
  .ant-table-body,
  .ant-table-content,
  .ant-table-scroll {
    ${visibleHorizontalScroll}
  }
`

/**
 * Keep an Actions column pinned to the right while the table scrolls
 * horizontally (same technique as the Receipting/Invoices orders table).
 *
 * Usage:
 *   - column definition: { ..., className: 'sticky-actions-col',
 *       onCell: () => ({ className: 'sticky-actions-cell' }) }
 *   - optional second column left of that: 'sticky-actions-col-prev' /
 *     'sticky-actions-cell-prev', plus 'has-sticky-prev' on the outer column;
 *     set --sticky-actions-prev-right on the wrapper to the outer width
 *   - interpolate `${stickyActionsColumn}` into the styled wrapper
 *     around the antd Table.
 *
 * Solid background + shadow so scrolling content never shows through.
 * Odd-row stripe keeps its tint via a striped-cell rule (antd v4 markup).
 */
export const stickyActionsColumn = css`
  .ant-table-thead > tr > th.sticky-actions-col,
  .ant-table-tbody > tr > td.sticky-actions-cell,
  .ant-table-tbody > tr > td.sticky-actions-col {
    position: sticky;
    right: 0;
    z-index: 2;
    background: #fff;
    box-shadow: -6px 0 8px -6px rgba(0, 0, 0, 0.18);
  }
  .ant-table-tbody > tr > td.sticky-actions-cell {
    background: #fafafa;
  }
  .ant-table-tbody > tr.ant-table-row-hover > td.sticky-actions-cell,
  .ant-table-tbody > tr:hover > td.sticky-actions-cell {
    background: #e6f7ff;
  }

  /**
   * Second sticky column immediately left of the rightmost sticky column.
   * Set --sticky-actions-prev-right on the wrapper to match the outer column
   * width (e.g. catalogue Action sitting left of Add to basket).
   */
  .ant-table-thead > tr > th.sticky-actions-col-prev,
  .ant-table-tbody > tr > td.sticky-actions-cell-prev,
  .ant-table-tbody > tr > td.sticky-actions-col-prev {
    position: sticky;
    right: var(--sticky-actions-prev-right, 120px);
    z-index: 1;
    background: #fff;
    box-shadow: -6px 0 8px -6px rgba(0, 0, 0, 0.18);
  }
  .ant-table-tbody > tr > td.sticky-actions-cell-prev {
    background: #fafafa;
  }
  .ant-table-tbody > tr.ant-table-row-hover > td.sticky-actions-cell-prev,
  .ant-table-tbody > tr:hover > td.sticky-actions-cell-prev {
    background: #e6f7ff;
  }
  /* Outer column: drop its left shadow when a prev sticky sits beside it */
  .ant-table-thead > tr > th.sticky-actions-col.has-sticky-prev,
  .ant-table-tbody > tr > td.sticky-actions-cell.has-sticky-prev,
  .ant-table-tbody > tr > td.sticky-actions-col.has-sticky-prev {
    box-shadow: none;
  }
`
